import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, useTheme } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Typography from "../typography";

export interface Attachment {
  name: string;
  url?: string;
}

interface AttachmentListProps {
  attachments: Attachment[];
  title?: string;
}

// Renders a list of attachments as clickable rows that open/download the file
// (URLs are public). Returns null when there are none.
const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  title,
}) => {
  const { t } = useTranslation();
  const heading = title ?? t("ui.attachments");
  const theme = useTheme();

  if (attachments.length === 0) {
    return null;
  }

  return (
    <Box>
      {heading && (
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, marginBottom: 8 }}
        >
          {heading}
        </Typography>
      )}
      <Stack spacing={0.5}>
        {attachments.map((a, idx) => {
          const row = (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                backgroundColor: theme.palette.action.hover,
                borderRadius: 1,
                px: 1,
                py: 0.5,
                ...(a.url
                  ? { cursor: "pointer", "&:hover": { opacity: 0.85 } }
                  : {}),
              }}
            >
              <InsertDriveFileIcon
                sx={{ fontSize: 16, color: theme.palette.action.active }}
              />
              <Typography
                variant="body2"
                style={{ flex: 1, minWidth: 0, wordBreak: "break-all" }}
              >
                {a.name}
              </Typography>
              {a.url && (
                <DownloadOutlinedIcon
                  sx={{ fontSize: 16, color: theme.palette.primary.main }}
                />
              )}
            </Stack>
          );

          return a.url ? (
            <a
              key={idx}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {row}
            </a>
          ) : (
            <Box key={idx}>{row}</Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default AttachmentList;
